/**
 * 사원증 결제 관리 — 고객사 화이트리스트 (FE 하드코딩)
 *
 * LNB 노출 조건과 페이지 진입 게이트가 같은 기준을 쓴다.
 * - LIVE(ENV=production)     : 아래 목록의 고객사만 허용
 * - 그 외(로컬/DEV/스테이지) : 전체 고객사 허용 (DEV 에서 검증 가능해야 하므로 진입 게이트도 함께 풀어둔다)
 */
export const EMPLOYEE_CARD_PAYMENT_ALLOWED_COM_IDS = [
  'B068366F-5EB8-1C7E-E79C-D438C8E32F9D', // 현대벤디스
  '5FEC5FBB-AD6F-4FA0-80F8-445D5A892530', // 현대 오토에버
  '019BBF79-4A97-7AF2-AE70-6024997D33FD' // 에이디테크
];

const isLive = process.env.ENV === 'production';

/**
 * LNB(components/LNB/Menu.jsx)가 읽는 노출 화이트리스트.
 * undefined 면 화이트리스트를 적용하지 않는다(= 전체 노출).
 */
export const EMPLOYEE_CARD_PAYMENT_VISIBLE_COM_IDS = isLive ? EMPLOYEE_CARD_PAYMENT_ALLOWED_COM_IDS : undefined;

/** 회사 게이트 */
export const isEmployeeCardPaymentAllowedCompany = (companyId?: string) =>
  !isLive || EMPLOYEE_CARD_PAYMENT_ALLOWED_COM_IDS.includes(companyId);

/**
 * LNB 노출에 쓰는 권한 코드(config/menuList.js 의 authCode). 사용자 관리와 같은 기준이다.
 * 메뉴에는 안 보이는데 URL 로는 들어가지는 일이 없도록 화면 진입 게이트도 같은 값을 쓴다.
 */
export const EMPLOYEE_CARD_PAYMENT_AUTH_CODES = [
  'employee:view',
  'employee:update',
  'employee:create',
  'employee:delete',
  'employee:search'
];

/**
 * 권한 보유 여부. '*:*' 는 전체 권한이라 항상 통과한다.
 * 인자는 storage 의 info.auth.auth (권한 코드 문자열 배열).
 */
export const hasEmployeeCardPaymentAuth = (authCodes?: string[]) =>
  (authCodes || []).some((code) => code === '*:*' || EMPLOYEE_CARD_PAYMENT_AUTH_CODES.indexOf(code) > -1);

/**
 * 사원증 결제정보 입력 제한 — 영문 대문자·숫자·하이픈(-) / 최대 30자 / 회사 내 중복 불가.
 * 관리 모달의 입력 제한과 엑셀 관리 화면의 안내 문구가 같은 값을 쓰도록 여기서만 관리한다.
 * 길이는 아래 정규화를 거친 값 기준이다.
 */
export const EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH = 30;

// 공백·보이지 않는 문자 10종 (U+0020, U+0009, U+000A, U+000D, U+00A0, U+200B, U+200C, U+200D, U+FEFF, U+00AD)
// ZWJ(U+200D)는 문자 클래스 안에 두면 앞뒤 글자를 잇는 것으로 읽혀(no-misleading-character-class) 따로 뺀다.
const CODE_REMOVE_PATTERN = /[ \t\n\r\u00A0\u200B\u200C\uFEFF\u00AD]|\u200D/g;
// U+002D 로 바꿀 하이픈 변종 4종 (U+2010, U+2013, U+2014, U+2212)
const CODE_HYPHEN_PATTERN = /[\u2010\u2013\u2014\u2212]/g;

/**
 * 사원증 결제정보 정규화 — 공백 제거 → 하이픈 변종 치환 → 대문자 변환 (PROD-23727).
 * 리더기 설정에 따라 같은 카드가 대소문자·공백만 다르게 올라오므로 같은 값으로 맞춘다.
 * 고칠 수 있는 차이만 정리하고, 허용 문자셋(A-Z 0-9 -) 밖의 글자는 그대로 남긴다.
 * (허용 여부는 서버가 판정한다 — 엑셀 일괄·상세 모달 모두 그대로 보내고 거부 사유를 보여준다)
 *
 * 사원증(IDCARD) 결제정보 전용이다. 다른 바코드 타입이나 ID·이름 같은 다른 컬럼에는 쓰지 않는다.
 */
export const normalizeEmployeeCardPaymentCode = (value?: unknown) => {
  if (value === undefined || value === null) return '';
  return String(value)
    .replace(CODE_REMOVE_PATTERN, '')
    .replace(CODE_HYPHEN_PATTERN, '-')
    .toUpperCase();
};

export const EMPLOYEE_CARD_PAYMENT_PAGE_SIZES = [10, 25, 50, 100, 200];
export const EMPLOYEE_CARD_PAYMENT_DEFAULT_PAGE_SIZE = 25;

export const EMPLOYEE_CARD_PAYMENT_PAGE_SIZE_OPTIONS = EMPLOYEE_CARD_PAYMENT_PAGE_SIZES.map((size) => ({
  key: size,
  value: size,
  text: `${size}씩 보기`
}));
